import { useEffect, useState } from 'react'
import Layout from 'containers/Layout'
import moment from 'moment'
import { get } from 'lodash'
import apiGet from 'lib/network/apiGet'
import apiDelete from 'lib/network/apiDelete'
import { useTranslation } from 'react-i18next'

import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'
import notify from 'components/atoms/Notify'
import PageFooter from 'components/paginationComponent'
import { IoSearch, IoTrashBin, IoAdd, IoCopyOutline } from 'react-icons/io5'
import { buildShortLinkUrl } from 'utils/shortLink'
import useModal from 'hooks/useModal'
import CreateShortLinkModal from './modals/CreateShortLinkModal'

const ShortLinks = () => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'

  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({})
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({})
  const [createModalKey, setCreateModalKey] = useState(0)

  const fetchList = async (page = 1) => {
    if (page === 1) {
      setLoading(true)
    }
    const params = {
      pageNumber: page,
      pageSize: 20,
      query: search || undefined,
      sortBy: sort.sortBy || undefined,
      sortOrder: sort.sortOrder || undefined,
    }
    const response = await apiGet('admin-short-links', params)
    if (response?.success && response?.data) {
      const list = response.data.shortLinks || []
      setItems(page === 1 ? list : [...items, ...list])
      setPagination(response.data.pagination || {})
    }
    setLoading(false)
  }

  const [ShortLinkCreateModal, openCreateModal] = useModal({
    content: <CreateShortLinkModal key={createModalKey} onSuccess={() => fetchList(1)} />,
    title: t('shortLinks.createSection'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '560px',
      minHeight: 'auto',
      minWidth: '320px',
    },
  })

  useEffect(() => {
    fetchList(1)
  }, [search, sort])

  const handleOpenCreateModal = () => {
    setCreateModalKey(k => k + 1)
    openCreateModal()
  }

  const copyText = text => {
    navigator.clipboard.writeText(text).then(
      () => {
        notify.success(t('shortLinks.copiedShortUrl'))
      },
      () => {
        notify.error(t('shortLinks.copyFailed'))
      }
    )
  }

  const handleDelete = id => {
    notify.deleteConfirm({
      title: t('shortLinks.deleteTitle'),
      message: t('shortLinks.deleteMessage'),
      onConfirm: async () => {
        const response = await apiDelete(`short-link/${id}`)
        if (response?.success) {
          notify.success(t('shortLinks.deletedSuccess'))
          fetchList(1)
        }
      },
    })
  }

  const columns = [
    {
      name: t('shortLinks.shortUrl'),
      width: '26%',
      key: 'shortUrl',
      sortable: true,
      rendered: item => {
        const full = buildShortLinkUrl(item.slug, item.shortUrl)
        if (!full) {
          return (
            <div className={isRTL ? 'mr-4' : 'ml-4'}>
              <span className="text-sm text-gray-400">{t('common.noData')}</span>
            </div>
          )
        }
        return (
          <div className={`flex items-center gap-2 flex-wrap min-w-0 ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <a href={full} target="_blank" rel="noopener noreferrer" title={full} className="text-sm text-blue-600 hover:text-blue-800 hover:underline break-all">
              {full}
            </a>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                e.preventDefault()
                copyText(full)
              }}
              className="text-[13px] text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1 shrink-0"
              aria-label={t('shortLinks.copyShortUrl')}
            >
              {t('shortLinks.copyUrlInline')}
              <IoCopyOutline className="h-3 w-3" />
            </button>
          </div>
        )
      },
    },
    {
      name: t('shortLinks.label'),
      width: '12%',
      key: 'label',
      sortable: true,
      rendered: item => (
        <div className={isRTL ? 'mr-4' : 'ml-4'}>
          <span className="text-sm font-normal text-gray-900">{item.label || ''}</span>
        </div>
      ),
    },
    {
      name: t('shortLinks.slug'),
      width: '10%',
      key: 'slug',
      sortable: true,
      rendered: item => (
        <div className={isRTL ? 'mr-4' : 'ml-4'}>
          <code className="text-[13px] bg-gray-100 px-2 py-0.5 rounded text-gray-900">{item.slug}</code>
        </div>
      ),
    },
    {
      name: t('shortLinks.clicks'),
      width: '8%',
      key: 'clickCount',
      rendered: item => (
        <div className={isRTL ? 'mr-4' : 'ml-4'}>
          <span className="text-sm font-normal text-gray-900">{item.clickCount ?? 0}</span>
        </div>
      ),
    },
    {
      name: t('shortLinks.lastClicked'),
      width: '12%',
      key: 'lastClickedAt',
      rendered: item => (
        <div className={isRTL ? 'mr-4' : 'ml-4'}>
          <span className="text-sm text-gray-500">{item.lastClickedAt ? moment(item.lastClickedAt).fromNow() : t('common.noData')}</span>
        </div>
      ),
    },
    {
      name: t('shortLinks.created'),
      width: '12%',
      key: 'createdAt',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        const label = createdTime ? moment(createdTime).fromNow() : t('common.noData')
        return (
          <div className={isRTL ? 'mr-4' : 'ml-4'}>
            <span className="text-sm text-gray-500">{label}</span>
          </div>
        )
      },
    },
    {
      name: t('shortLinks.updated'),
      width: '12%',
      key: 'updatedAt',
      sortable: true,
      rendered: item => {
        const updatedTime = get(item, 'updatedAt', '')
        const label = updatedTime ? moment(updatedTime).fromNow() : t('common.noData')
        return (
          <div className={isRTL ? 'mr-4' : 'ml-4'}>
            <span className="text-sm text-gray-500">{label}</span>
          </div>
        )
      },
    },

    {
      name: '',
      width: '8%',
      key: 'actions',
      rendered: item => (
        <div className={isRTL ? 'mr-4' : 'ml-4'}>
          <button type="button" className="text-rose-600 hover:text-rose-800 p-1" onClick={() => handleDelete(item._id)} aria-label={t('shortLinks.delete')}>
            <IoTrashBin className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <Layout
      breadcrumbs={[{ name: t('shortLinks.breadcrumb'), pathname: '/short-links' }]}
      headerRight={
        <div className={`flex items-center justify-end ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="mt-1 relative rounded-md shadow-sm flex-grow">
            <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
            <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
              <IoSearch className={`${isRTL ? 'ml-3' : 'mr-3'} h-4 w-4 text-gray-400`} aria-hidden="true" />
            </div>
            <input
              onChange={e => setSearch(e.target.value)}
              value={search}
              type="text"
              name="search"
              id="short-links-search"
              className={`block ${isRTL ? 'pr-9' : 'pl-9'} shadow-none sm:text-sm bg-gray-100 border-0 focus:outline-none rounded-sm`}
              placeholder={t('shortLinks.searchPlaceholder')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className={`mt-1 relative rounded-md shadow-sm ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <Button type="button" onClick={handleOpenCreateModal} className="inline-flex items-center gap-1.5">
              <IoAdd className="h-5 w-5 shrink-0" aria-hidden />
              {t('shortLinks.createButton')}
            </Button>
          </div>
        </div>
      }
      title={t('shortLinks.title')}
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {!loading ? (
                  <>
                    {items?.length === 0 ? (
                      <EmptyState v2 description={t('shortLinks.noItems')} />
                    ) : (
                      <Table
                        hasMore={pagination?.currentPage !== pagination?.totalPages}
                        fetchData={() => fetchList((pagination?.currentPage || 0) + 1)}
                        sortBy={sort.sortBy}
                        sortOrder={sort.sortOrder}
                        handleSort={(sortBy, sortOrder) => setSort({ sortBy, sortOrder })}
                        data={items}
                        columns={columns}
                      />
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
      <ShortLinkCreateModal />
    </Layout>
  )
}

export default ShortLinks
